import {
  Button,
  Center,
  Drawer,
  Flex,
  Grid,
  Loader,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useEffect, useState } from "react";
import { useDisclosure } from "@mantine/hooks";
import { fetchWithAuthentication } from "../utilities/FetchUtil";
import { AiFeatureTypes, Prompt, PromptDTO } from "../models/Prompt";
import { convertToLocalDateTime } from "../utilities/DateUtil";

const ManagePrompts = () => {
  const [createText, setCreateText] = useState("");
  const [updateText, setUpdateText] = useState("");
  const [createTextColor, setCreateTextColor] = useState("green");
  const [updateTextColor, setUpdateTextColor] = useState("green");
  const [createIsLoading, setCreateIsLoading] = useState(false);
  const [updateIsLoading, setUpdateIsLoading] = useState(false);
  const [listIsLoading, setListIsLoading] = useState(false);
  const [aiPromptsList, setAiPromptsList] = useState<Prompt[]>([]);
  const [selectedAiPrompt, setSelectedAiPrompt] = useState<Prompt>({
    featureType: "",
    systemPrompt: "",
    userPrompt: "",
    version: "",
    createdAt: "",
  });
  const [aiPromptClicked, setAiPromptClicked] = useState<number | null>(null);

  const [updateDrawerOpened, updateDrawerHandlers] = useDisclosure(false);
  const [createDrawerOpened, createDrawerHandlers] = useDisclosure(false);

  const aiPromptForm = useForm({
    mode: "uncontrolled",
    initialValues: {
      featureType: "",
      systemPrompt: "",
      userPrompt: "",
    },
    validate: {
      featureType: (value: string) =>
        value.trim().length === 0 ? "Feature type must not be empty." : null,
      systemPrompt: (value: string) =>
        value.length > 10000
          ? "System prompt must be 10,000 characters or fewer."
          : null,
      userPrompt: (value: string) =>
        value.length > 10000
          ? "User prompt must be 10,000 characters or fewer."
          : null,
    },
  });

  const updateForm = useForm({
    mode: "uncontrolled",
    initialValues: {
      featureType: "",
      systemPrompt: selectedAiPrompt.systemPrompt,
      userPrompt: selectedAiPrompt.userPrompt,
    },
    validate: {
      featureType: (value: string) =>
        value.trim().length === 0 ? "Feature type must not be empty." : null,
      systemPrompt: (value: string) =>
        value.length > 10000
          ? "System prompt must be 10,000 characters or fewer."
          : null,
      userPrompt: (value: string) =>
        value.length > 10000
          ? "User prompt must be 10,000 characters or fewer."
          : null,
    },
  });

  const createPrompt = async () => {
    setCreateIsLoading(true);
    const request: PromptDTO = {
      featureType: aiPromptForm.getValues().featureType,
      systemPrompt: aiPromptForm.getValues().systemPrompt,
      userPrompt: aiPromptForm.getValues().userPrompt,
    };
    const response = await fetchWithAuthentication("/prompts", "POST", request);
    if (response.ok) {
      setCreateTextColor("green");
      setCreateText("New prompt successfully created.");
      setCreateIsLoading(false);
      getAiPrompts();
    } else if (response.status === 409) {
      setCreateTextColor("red");
      setCreateText(
        `There already exists a config with feature type '${aiPromptForm.getValues().featureType}'`,
      );
      setCreateIsLoading(false);
    } else {
      setCreateTextColor("red");
      setCreateText(
        "Something went wrong while attempting to create new prompt",
      );
      setCreateIsLoading(false);
    }
    aiPromptForm.reset();
  };

  const updatePrompt = async () => {
    setUpdateIsLoading(true);
    const request: PromptDTO = {
      featureType: selectedAiPrompt.featureType,
      systemPrompt: updateForm.getValues().systemPrompt,
      userPrompt: updateForm.getValues().userPrompt,
    };
    const response = await fetchWithAuthentication("/prompts", "PUT", request);
    if (response.ok) {
      setUpdateTextColor("green");
      setUpdateText("AI prompt successfully updated.");
      setUpdateIsLoading(false);
      getAiPrompts();
    } else {
      setUpdateTextColor("red");
      setUpdateText("An issue occurred while trying to update AI prompt.");
      setUpdateIsLoading(false);
    }
  };

  const getAiPrompts = async () => {
    setListIsLoading(true);
    const response = await fetchWithAuthentication("/prompts", "GET", null);
    if (response.ok) {
      const json = await response.json();
      setAiPromptsList(json.aiPrompts);
      setListIsLoading(false);
    } else {
      console.log("Something went wrong fetching prompts");
      setAiPromptsList([]);
      setListIsLoading(false);
    }
  };

  useEffect(() => {
    getAiPrompts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const featureTypeOptions = Object.keys(AiFeatureTypes).map((key) => ({
    value: key,
    label: AiFeatureTypes[key as keyof typeof AiFeatureTypes],
  }));

  return (
    <>
      <Drawer
        offset={8}
        radius="md"
        position="right"
        opened={updateDrawerOpened}
        onClose={() => {
          updateDrawerHandlers.close();
          setUpdateText("");
        }}
        title="Update AI prompt"
      >
        <form onSubmit={updateForm.onSubmit(updatePrompt)}>
          <Stack w={400}>
            <TextInput
              label="Prompt feature type"
              value={selectedAiPrompt.featureType}
              disabled
            />
            <Textarea
              label="System prompt"
              {...updateForm.getInputProps("systemPrompt")}
              key={`update-${updateForm.key("systemPrompt")}`}
              autosize
              minRows={3}
              maxRows={10}
              placeholder="The prompt to tell the AI what it must do"
            />
            <Textarea
              label="User prompt"
              {...updateForm.getInputProps("userPrompt")}
              key={`update-${updateForm.key("userPrompt")}`}
              autosize
              minRows={3}
              maxRows={10}
              placeholder="The user's data that will be used"
            />
            <Button type="submit">Update</Button>
            <Center mih={50}>
              {updateIsLoading ? (
                <Loader />
              ) : (
                <Text c={updateTextColor}>{updateText}</Text>
              )}
            </Center>
          </Stack>
        </form>
      </Drawer>

      <Drawer
        offset={8}
        radius="md"
        position="left"
        opened={createDrawerOpened}
        onClose={() => {
          createDrawerHandlers.close();
          setCreateText("");
        }}
        title="Create AI prompt"
      >
        <form onSubmit={aiPromptForm.onSubmit(createPrompt)}>
          <Stack w={400}>
            <Select
              label="Prompt feature type"
              {...aiPromptForm.getInputProps("featureType")}
              key={aiPromptForm.key("featureType")}
              placeholder="The purpose of the prompt"
              data={featureTypeOptions}
            />
            <Textarea
              label="System prompt"
              {...aiPromptForm.getInputProps("systemPrompt")}
              key={aiPromptForm.key("systemPrompt")}
              autosize
              minRows={3}
              maxRows={10}
              placeholder="The prompt to tell the AI what it must do"
            />
            <Textarea
              label="User prompt"
              {...aiPromptForm.getInputProps("userPrompt")}
              key={aiPromptForm.key("userPrompt")}
              autosize
              minRows={3}
              maxRows={10}
              placeholder="The user's data that will be used"
            />
            <Button type="submit">Create</Button>
            <Center mih={50}>
              {createIsLoading ? (
                <Loader />
              ) : (
                <Text c={createTextColor}>{createText}</Text>
              )}
            </Center>
          </Stack>
        </form>
      </Drawer>

      <Stack justify="center" w="100%">
        <Paper p="lg" shadow="lg" radius="md" w="100%">
          <Flex direction="column" gap="lg" align="center" w="100%">
            <Title order={3}>Manage AI prompts</Title>
            <Button onClick={createDrawerHandlers.open}>
              Create new prompt
            </Button>
          </Flex>
        </Paper>
        <Paper p="md" shadow="lg" radius="md">
          <Grid>
            <Grid.Col span={2} fw={700}>
              Feature type
            </Grid.Col>
            <Grid.Col span={3} fw={700}>
              System prompt
            </Grid.Col>
            <Grid.Col span={3} fw={700}>
              User prompt
            </Grid.Col>
            <Grid.Col span={1} fw={700}>
              Version
            </Grid.Col>
            <Grid.Col span={2} fw={700}>
              Created
            </Grid.Col>
            <Grid.Col span={1} fw={700}>
              Update
            </Grid.Col>
          </Grid>
        </Paper>
        <Stack>
          {listIsLoading && (
            <Center>
              <Loader />
            </Center>
          )}
          {aiPromptsList.length === 0 && !listIsLoading && (
            <Center>
              <Text>No AI prompts found.</Text>
            </Center>
          )}
          {aiPromptsList.map((aiPrompt, index) => {
            return (
              <Paper
                key={`aiPrompt${index}`}
                p="md"
                shadow="lg"
                radius="md"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  if (index === aiPromptClicked) {
                    setAiPromptClicked(null);
                  } else {
                    setAiPromptClicked(index);
                  }
                }}
              >
                <Grid align="center">
                  <Grid.Col span={2}>
                    {
                      AiFeatureTypes[
                        aiPrompt.featureType as keyof typeof AiFeatureTypes
                      ]
                    }
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Text lineClamp={aiPromptClicked === index ? 100 : 2}>
                      {aiPrompt.systemPrompt}
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Text lineClamp={aiPromptClicked === index ? 100 : 2}>
                      {aiPrompt.userPrompt}
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={1}>{aiPrompt.version}</Grid.Col>
                  <Grid.Col span={2}>
                    {convertToLocalDateTime(aiPrompt.createdAt)}
                  </Grid.Col>
                  <Grid.Col span={1}>
                    <Button
                      onClick={() => {
                        updateForm.reset();
                        updateForm.setValues(aiPrompt);
                        setSelectedAiPrompt(aiPrompt);
                        updateDrawerHandlers.open();
                      }}
                    >
                      Edit
                    </Button>
                  </Grid.Col>
                </Grid>
              </Paper>
            );
          })}
        </Stack>
      </Stack>
    </>
  );
};

export default ManagePrompts;
